/**
 * PlayerDraftSection — lobby surface for the player-roster draft.
 *
 * Phase 1: settings (per-position required counts) + idle placeholder. The
 * actual pool + snake/pack pick flow ships in Phase 2 — until then the host
 * can configure the draft shape but Start Draft is gated.
 *
 * Mirrors DeckDraftSection's three-state structure so the lobby can render
 * both side-by-side or in series under the `draft_order` array.
 *
 * Pure UI: all state comes via props, all actions out via callbacks.
 */

import React, { useState } from "react";
import { Dices, Plus, Minus, Users } from "lucide-react";

import { SectionLabel, CollapseToggle } from "@/components/lobby/primitives";
import { POSITIONS } from "@/constants/positions";

// Roster sums above/below these bounds get clamped server-side too; the UI
// just disables the +/- buttons at the edges.
const MIN_PER_POSITION = 0;
const MAX_PER_POSITION = 5;
// Pitchers can't be 0 — defensive baseball needs a thrower. The UI floors
// pitcher count at 1 so the host can't make an unstartable game.
const MIN_PITCHER_COUNT = 1;
// `Flex` is a wildcard pick slot — no position-coverage minimum. We render
// it alongside the real positions in the counter grid so the host can grow
// or shrink it like any other slot.
const ROSTER_SLOTS = [...POSITIONS, "Flex"];

export default function PlayerDraftSection({
  positionCounts = {},
  onPositionCountChange,
  playerDraftPhase = "idle",
  disabled = false,
  // Host-only Start Draft action.
  isHome = false,
  canStartDraft = false,
  onStartDraft,
  // Phase 1 doesn't wire ready-up yet; placeholders carry the props so the
  // section can grow into them in Phase 2 without an API change here.
  role = "home", draftReadyHome = false, draftReadyAway = false, onDraftReady,
  // Once Phase 2 lands and the pool is initialized this hides the placeholder.
  picksActive = false,
  // Drag-handle slot — rendered into the section header so the parent can wire
  // up @dnd-kit / sortable. Optional; section renders fine without it.
  dragHandle = null,
  // Optional handler for the post-draft "View Rosters" button. When set,
  // the complete-state block renders a CTA that opens the parent's roster
  // modal; when omitted, the section just shows a confirmation line.
  onViewRosters = null,
}) {
  const [settingsOpen, setSettingsOpen] = useState(true);
  // Sum of per-slot counts = total picks each side must make. Includes the
  // Flex wildcard so the host sees the full roster size accurately.
  const totalPicks = ROSTER_SLOTS.reduce((sum, p) => sum + (positionCounts[p] ?? 0), 0);

  // Settings lock once the player draft has started. Mirrors DeckDraftSection.
  const settingsLocked = playerDraftPhase !== "idle";
  const settingsLockReason = playerDraftPhase === "complete"
    ? "Draft complete — settings locked."
    : "Draft in progress — settings locked.";

  const bumpCount = (pos, delta) => {
    const cur = positionCounts[pos] ?? 0;
    const floor = pos === "P" ? MIN_PITCHER_COUNT : MIN_PER_POSITION;
    const next = Math.max(floor, Math.min(MAX_PER_POSITION, cur + delta));
    if (next !== cur) onPositionCountChange?.(pos, next);
  };

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 space-y-4 mb-6">
      <div className="flex items-center justify-between">
        <SectionLabel>Player Draft</SectionLabel>
        {dragHandle}
      </div>

      {/* Idle / drafting placeholder. Phase 2 will swap this for the actual
          pick UI once `picksActive` flips true. */}
      {!picksActive && (
        <div className="rounded-xl border border-slate-700/50 bg-slate-900/30 px-6 py-10 flex flex-col items-center justify-center gap-3 text-center">
          <Dices size={32} className="text-slate-700" />
          <div>
            <p className="font-black text-base text-slate-500">Player Card Draft</p>
            <p className="text-xs text-slate-600 mt-1 max-w-xs">
              6 players in each pack, both teams pick 1 each, the rest are discarded.
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5 justify-center mt-1">
            <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-300">
              {totalPicks} picks / side
            </span>
            <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-300">
              6-card packs
            </span>
            <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-300">
              Snake order
            </span>
          </div>

          {/* Phase 1: the start button is gated until Phase 2 wires up the
              pool. Show a clear "coming soon" hint instead of a dead button. */}
          {playerDraftPhase === "idle" && isHome && (
            <button
              type="button"
              onClick={onStartDraft}
              disabled={!canStartDraft || totalPicks <= 0}
              className="mt-2 px-6 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed text-black font-black text-sm tracking-wide transition-all"
            >
              {totalPicks <= 0 ? "Add at least one roster slot" : "Start Draft →"}
            </button>
          )}
          {playerDraftPhase === "idle" && !isHome && (
            <span className="text-[11px] text-slate-500 font-medium">Waiting for host to start the draft…</span>
          )}

          {/* Drafting — per-player Ready cards (pre-pool state). Once the
              server initializes the pool the parent flips `picksActive` and
              this block hides; the dedicated /player-draft page takes over. */}
          {playerDraftPhase === "drafting" && (
            <div className="w-full flex gap-2 mt-2">
              {[
                { side: "home", label: "Home", ready: draftReadyHome },
                { side: "away", label: "Away", ready: draftReadyAway },
              ].map(({ side, label, ready }) => {
                const mine = role === side;
                return (
                  <div key={side}
                    className={`flex-1 rounded-xl border px-4 py-3 flex items-center justify-between gap-3
                      ${ready ? "bg-emerald-500/10 border-emerald-500/40" : "bg-slate-800/60 border-slate-700/60"}`}>
                    <div className="text-left">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</p>
                      <p className={`text-xs font-bold ${ready ? "text-emerald-400" : "text-slate-400"}`}>
                        {ready ? "Ready to draft ✓" : "Not ready"}
                      </p>
                    </div>
                    {mine && (
                      <button
                        type="button"
                        onClick={() => onDraftReady?.(!ready)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-black transition-colors
                          ${ready ? "bg-slate-700 hover:bg-slate-600 text-white" : "bg-amber-500 hover:bg-amber-400 text-black"}`}
                      >
                        {ready ? "Unready" : "Ready Up"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {playerDraftPhase === "complete" && (
            <div className="mt-2 flex flex-col items-center gap-3">
              <div className="flex items-center gap-2 text-emerald-400 text-xs font-black">
                ⚾ Draft Complete · rosters populated
              </div>
              {onViewRosters && (
                <button
                  type="button"
                  onClick={onViewRosters}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-black text-xs tracking-wide transition-all"
                >
                  <Users size={13} />
                  View Rosters
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Settings — host-only collapsible. Position-count grid drives the
          per-position required picks (and therefore the total pick count). */}
      {!disabled && (
        <>
          <CollapseToggle open={settingsOpen} onToggle={() => setSettingsOpen(o => !o)} label="Roster Requirements" />
          {settingsOpen && (
            <div className="space-y-3 pt-1">
              {settingsLocked && (
                <p className="text-[10px] font-bold text-amber-500/80 italic">{settingsLockReason}</p>
              )}
              <p className="text-[10px] text-slate-600">
                Roster shape. Pitchers can't drop below 1. Bench/Bullpen slots are wildcards —
                any pick beyond the position minimums fills them.
              </p>
              <div className={`grid grid-cols-2 sm:grid-cols-3 gap-2 ${settingsLocked ? "opacity-60 pointer-events-none" : ""}`}>
                {ROSTER_SLOTS.map(pos => {
                  const cur = positionCounts[pos] ?? 0;
                  const floor = pos === "P" ? MIN_PITCHER_COUNT : MIN_PER_POSITION;
                  const isFlex = pos === "Flex";
                  return (
                    <div key={pos} className={`flex items-center justify-between gap-2 px-3 py-2 rounded-xl border ${
                      isFlex ? "bg-blue-500/5 border-blue-500/30" : "bg-slate-800/80 border-slate-700"
                    }`}>
                      <span className={`text-xs font-black ${isFlex ? "text-blue-300 shrink-0" : "text-slate-300 w-12"}`}>{isFlex ? "Bench/BP" : pos}</span>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => bumpCount(pos, -1)}
                          disabled={cur <= floor}
                          className="w-6 h-6 rounded-md bg-slate-900/80 border border-slate-700 text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
                        >
                          <Minus size={12} />
                        </button>
                        <span className="text-sm font-black text-white w-5 text-center tabular-nums">{cur}</span>
                        <button
                          type="button"
                          onClick={() => bumpCount(pos, +1)}
                          disabled={cur >= MAX_PER_POSITION}
                          className="w-6 h-6 rounded-md bg-slate-900/80 border border-slate-700 text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-500">
                Total picks per side: <span className="font-black text-slate-300">{totalPicks}</span>
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
